/**
 * Outlook / PreFlight Risk component.
 *
 * "Are critical pre-launch / readiness items stalled?"
 *
 * Consumes PreflightItem state from the project's checklist. Critical items
 * stuck in `blocked` are leading indicators of imminent missed launch dates
 * or downstream Schedule reliability collapse — if you can't unblock the
 * critical path resource, the whole timeline slips.
 *
 * Score formula: penalty proportional to fraction of critical items in
 * `blocked` state.
 *
 *   blocked_critical = items with priority='critical' AND state='blocked'
 *   total_critical   = items with priority='critical' AND state != 'withdrawn'
 *
 *   if total_critical == 0:
 *     N/A (project doesn't track critical preflight items)
 *   else:
 *     score = max(0, 100 - (blocked_critical / total_critical) * 100)
 *
 * Per Skip's call (DEV-846 framing discussion #3): when a project has no
 * PreFlight items configured at all, return N/A — not score 100. Absence
 * of data ≠ absence of risk; defaulting to 100 would unfairly inflate the
 * Outlook composite for projects that just don't use the PreFlight feature.
 *
 * Withdrawn items don't count toward the denominator (they're explicitly
 * removed from scope by the team — not a blocker).
 *
 * See: DEV-846 epic, DEV-847.
 */

import type { ComponentResult, DataReliabilityInputs } from '../types';

export function computePreflightRisk(
  inputs: DataReliabilityInputs,
): ComponentResult {
  const items = inputs.preflight_items;

  // Only critical, non-withdrawn items count toward the signal.
  const relevant = items.filter(
    (i) => i.priority === 'critical' && i.state !== 'withdrawn',
  );

  if (relevant.length === 0) {
    return {
      key: 'preflight_risk',
      score: null,
      applicable: false,
      reason: 'No critical PreFlight items tracked — risk cannot be measured',
      explain: 'Project has no critical pre-launch items.',
    };
  }

  const blocked = relevant.filter((i) => i.state === 'blocked').length;
  const blockedRatio = blocked / relevant.length;
  const score = Math.round(Math.max(0, 100 - blockedRatio * 100));

  let explain: string;
  if (blocked === 0) {
    explain = `No critical items blocked (${relevant.length} tracked).`;
  } else if (blocked === relevant.length) {
    explain = `All ${relevant.length} critical items blocked.`;
  } else {
    explain = `${blocked} of ${relevant.length} critical items blocked.`;
  }

  return {
    key: 'preflight_risk',
    score,
    applicable: true,
    explain,
  };
}
